import { closeIcon, personIcon } from './icons';
import { t } from './i18n';

const ROOT_CLASS = 'flix-chat-widget__teaser';

// What the teaser shows: a message, plus an optional real support person to
// attribute it to (their Discord avatar if we have the URL, otherwise the
// first letter of their name in a circle — Discord/Slack style). With no
// assigned staff member it falls back to the generic support avatar (the
// same person glyph the header uses), never a fake name.
export interface GreetingTeaserContent {
  message: string;
  presenterName: string | null;
  avatarUrl: string | null;
  avatarInitial: string | null;
}

export interface GreetingTeaserHandlers {
  onOpen: () => void;
  onDismiss: () => void;
}

export interface GreetingTeaser {
  element: HTMLElement;
  render(content: GreetingTeaserContent): void;
  show(): void;
  hide(onHidden?: () => void): void;
  isVisible(): boolean;
  destroy(): void;
}

const HIDE_ANIMATION_FALLBACK_MS = 260;

export function createGreetingTeaser(handlers: GreetingTeaserHandlers): GreetingTeaser {
  const element = document.createElement('div');
  element.className = ROOT_CLASS;
  element.setAttribute('role', 'dialog');
  element.setAttribute('aria-label', t('teaserAria'));
  element.hidden = true;

  const bubble = document.createElement('div');
  bubble.className = `${ROOT_CLASS}-bubble`;

  // The whole card (minus the × hit area) opens the widget — a button so it
  // gets keyboard activation and focus-visible for free, exactly like the
  // launcher and reconnect banner.
  const main = document.createElement('button');
  main.type = 'button';
  main.className = `${ROOT_CLASS}-main`;

  const avatar = document.createElement('span');
  avatar.className = `${ROOT_CLASS}-avatar`;

  const statusDot = document.createElement('span');
  statusDot.className = `${ROOT_CLASS}-status-dot ${ROOT_CLASS}-status-dot--online`;

  const content = document.createElement('span');
  content.className = `${ROOT_CLASS}-content`;

  const nameLine = document.createElement('span');
  nameLine.className = `${ROOT_CLASS}-name`;
  nameLine.hidden = true;

  const messageLine = document.createElement('span');
  messageLine.className = `${ROOT_CLASS}-message`;

  content.append(nameLine, messageLine);
  main.append(avatar, statusDot, content);

  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.className = `${ROOT_CLASS}-close`;
  closeButton.setAttribute('aria-label', t('teaserDismiss'));
  closeButton.append(closeIcon());

  const arrow = document.createElement('span');
  arrow.className = `${ROOT_CLASS}-arrow`;
  arrow.setAttribute('aria-hidden', 'true');

  bubble.append(closeButton, main);
  element.append(bubble, arrow);

  main.addEventListener('click', () => handlers.onOpen());
  closeButton.addEventListener('click', (event) => {
    // Never let the × also trigger the card's open handler.
    event.stopPropagation();
    handlers.onDismiss();
  });

  let visible = false;
  let hideFallbackId: ReturnType<typeof setTimeout> | null = null;

  function clearHideListeners(handler: (event: AnimationEvent) => void): void {
    if (hideFallbackId !== null) {
      clearTimeout(hideFallbackId);
      hideFallbackId = null;
    }
    element.removeEventListener('animationend', handler);
  }

  return {
    element,

    render(teaser: GreetingTeaserContent): void {
      // Avatar: real photo → coloured initial → generic support glyph.
      if (teaser.avatarUrl) {
        const img = document.createElement('img');
        img.className = `${ROOT_CLASS}-avatar-image`;
        img.src = teaser.avatarUrl;
        img.alt = '';
        avatar.replaceChildren(img);
        avatar.classList.remove(`${ROOT_CLASS}-avatar--initial`);
      } else if (teaser.avatarInitial) {
        avatar.textContent = teaser.avatarInitial.toUpperCase();
        avatar.classList.add(`${ROOT_CLASS}-avatar--initial`);
      } else {
        avatar.replaceChildren(personIcon());
        avatar.classList.remove(`${ROOT_CLASS}-avatar--initial`);
      }

      // Re-add the status dot (replaceChildren/textContent above cleared it).
      avatar.append(statusDot);

      if (teaser.presenterName) {
        nameLine.textContent = `${teaser.presenterName} • ${t('teaserOnline')}`;
        nameLine.hidden = false;
      } else {
        nameLine.hidden = true;
      }

      // Preserves author-intended line breaks (the messages are two short
      // lines) without ever interpreting message text as markup.
      messageLine.textContent = teaser.message;
    },

    show(): void {
      if (visible) {
        return;
      }
      visible = true;
      element.hidden = false;
      element.classList.remove(`${ROOT_CLASS}--hiding`);
      // Force a reflow so re-showing after a hide restarts the entry
      // animation rather than being coalesced away.
      void element.offsetHeight;
      element.classList.add(`${ROOT_CLASS}--visible`);
    },

    hide(onHidden?: () => void): void {
      if (!visible) {
        onHidden?.();
        return;
      }
      visible = false;

      const finish = (): void => {
        clearHideListeners(handleAnimationEnd);
        element.classList.remove(`${ROOT_CLASS}--visible`, `${ROOT_CLASS}--hiding`);
        element.hidden = true;
        onHidden?.();
      };

      const handleAnimationEnd = (event: AnimationEvent): void => {
        if (event.target === element) {
          finish();
        }
      };

      element.classList.add(`${ROOT_CLASS}--hiding`);
      element.addEventListener('animationend', handleAnimationEnd);
      // Fallback for reduced-motion (animations collapsed to ~0ms may not
      // reliably emit animationend) and any missed event.
      hideFallbackId = setTimeout(finish, HIDE_ANIMATION_FALLBACK_MS);
    },

    isVisible(): boolean {
      return visible;
    },

    destroy(): void {
      if (hideFallbackId !== null) {
        clearTimeout(hideFallbackId);
        hideFallbackId = null;
      }
      element.remove();
    },
  };
}
